import type { UserDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { Modal } from '../../components/Modal';
import { ApiError, errorMessage } from '../../lib/api';
import { dmsApi, usersApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { displayName, dmName } from '../../store/selectors';
import { toast, useUi } from '../../store/ui';
import { freeSlots, matchesUser, openDm, useContacts, useDebounced } from './dmUtils';
import './dms.css';

/**
 * Nutzer suchen und auswählen: 1 → Direktnachricht, mehrere → Gruppen-DM (Limit `maxGroupDmParticipants`).
 * Mit `channelId` wird eine bestehende Gruppe erweitert (PUT /dms/:id/recipients/:userId).
 */
export function NewDmDialog({ onClose, channelId }: { onClose: () => void; /** Bestehende Gruppe erweitern */ channelId?: string }) {
  const navigate = useNavigate();
  const meId = useAppStore((s) => s.me?.id);
  const group = useAppStore((s) => (channelId ? s.channels[channelId] : undefined));
  const maxParticipants = useAppStore((s) => s.limits.maxGroupDmParticipants);
  const contacts = useContacts();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<UserDto[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const debounced = useDebounced(query.trim(), 250);
  const search = useQuery({
    queryKey: ['user-search', debounced],
    queryFn: () => usersApi.search(debounced, 25),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const existingIds = new Set((group?.recipients ?? []).map((u) => u.id));
  if (meId) existingIds.add(meId);
  // Teilnehmende inkl. mir: neue Gruppe = 1 (ich), bestehende = aktuelle Anzahl.
  const participants = group ? existingIds.size : 1;
  const slots = freeSlots(maxParticipants, participants);
  const remaining = slots - selected.length;

  const local = contacts.filter((c) => matchesUser(c.user, query)).map((c) => c.user);
  const remote = debounced.length >= 2 ? (search.data ?? []) : [];
  const seen = new Set<string>();
  const candidates = [...local, ...remote].filter((u) => {
    if (seen.has(u.id) || u.id === meId || u.isShadow) return false;
    seen.add(u.id);
    return true;
  });

  const toggle = (u: UserDto) => {
    setError(null);
    setSelected((cur) => (cur.some((x) => x.id === u.id) ? cur.filter((x) => x.id !== u.id) : [...cur, u]));
  };

  const go = (chId: string) => {
    useUi.getState().setNavOpen(false);
    onClose();
    void navigate(paths.dm(chId));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (group) {
        for (const u of selected) await dmsApi.addRecipient(group.id, u.id);
        // CHANNEL_RECIPIENT_ADD kommt per Gateway; lokal sofort übernehmen.
        useAppStore.getState().update((d) => {
          const ch = d.channels[group.id];
          if (!ch) return;
          for (const u of selected) {
            d.users[u.id] ??= u;
            if (!(ch.recipients ?? []).some((x) => x.id === u.id)) ch.recipients = [...(ch.recipients ?? []), u];
          }
        });
        toast.success(selected.length === 1 ? `${displayName(selected[0])} hinzugefügt` : `${selected.length} Personen hinzugefügt`);
        go(group.id);
      } else {
        const ch = await openDm(
          selected.map((u) => u.id),
          selected.length > 1 ? name : undefined,
        );
        go(ch.id);
      }
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'DM_BLOCKED' ? 'Diese Person nimmt keine Direktnachrichten von dir an.' : errorMessage(err));
      setBusy(false);
    }
  };

  const title = group ? `Zu „${dmName(group, meId)}" hinzufügen` : 'Neue Direktnachricht';
  const submitLabel = group ? 'Hinzufügen' : selected.length > 1 ? 'Gruppe erstellen' : 'Direktnachricht öffnen';

  return (
    <Modal title={title} onClose={onClose} testId="new-dm-dialog">
      <form className="stack new-dm" onSubmit={(e) => void submit(e)}>
        <p className="small muted">
          {Number.isFinite(slots)
            ? remaining > 0
              ? `Du kannst noch ${remaining} ${remaining === 1 ? 'Person' : 'Personen'} hinzufügen.`
              : 'Die Gruppe ist voll.'
            : 'Wähle eine Person für eine Direktnachricht oder mehrere für eine Gruppe.'}
        </p>
        {selected.length > 0 && (
          <ul className="new-dm-chips" role="list" aria-label="Ausgewählt">
            {selected.map((u) => (
              <li key={u.id} className="new-dm-chip" data-testid="new-dm-selected" data-user-id={u.id}>
                <Avatar user={u} size={18} />
                <span>{displayName(u)}</span>
                <button type="button" className="icon-btn icon-btn-xs" onClick={() => toggle(u)} aria-label={`${displayName(u)} entfernen`} data-testid="new-dm-remove">
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="field">
          <label htmlFor={`${id}-q`}>Nutzer suchen</label>
          <input
            id={`${id}-q`}
            className="input"
            type="search"
            value={query}
            autoFocus
            autoComplete="off"
            placeholder="Benutzername oder Anzeigename"
            onChange={(e) => setQuery(e.target.value)}
            aria-controls={`${id}-list`}
            data-testid="new-dm-search"
          />
        </div>
        <div
          id={`${id}-list`}
          className="new-dm-results scroll"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Personen"
          aria-busy={search.isFetching}
          data-testid="new-dm-results"
        >
          {candidates.map((u) => {
            const on = selected.some((x) => x.id === u.id);
            const member = existingIds.has(u.id);
            const disabled = member || (!on && remaining <= 0);
            return (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={on}
                aria-disabled={disabled}
                disabled={disabled}
                className="new-dm-user"
                onClick={() => toggle(u)}
                data-testid="new-dm-user"
                data-user-id={u.id}
              >
                <Avatar user={u} size={32} showStatus />
                <span className="new-dm-user-text">
                  <span className="truncate">{displayName(u)}</span>
                  <span className="small faint truncate">
                    {u.username}
                    {member ? ' · bereits in der Gruppe' : ''}
                  </span>
                </span>
                <span className="new-dm-check" aria-hidden="true">
                  {on && <Check size={14} />}
                </span>
              </button>
            );
          })}
          {candidates.length === 0 && (
            <p className="empty small" data-testid="new-dm-empty">
              {debounced.length >= 2
                ? search.isFetching
                  ? 'Suche …'
                  : 'Niemand gefunden.'
                : query.trim().length === 1
                  ? 'Mindestens 2 Zeichen eingeben.'
                  : 'Noch keine Kontakte – suche nach einem Benutzernamen.'}
            </p>
          )}
        </div>
        {!group && selected.length > 1 && (
          <div className="field">
            <label htmlFor={`${id}-name`}>Gruppenname (optional)</label>
            <input id={`${id}-name`} className="input" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} data-testid="new-dm-name" />
          </div>
        )}
        {error && (
          <p className="error-text small" role="alert" data-testid="new-dm-error">
            {error}
          </p>
        )}
        <div className="row new-dm-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || selected.length === 0} data-testid="new-dm-submit">
            {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
